import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ViewModule,
  TableChart,
  CalendarToday
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../config/axios';
import dayjs from 'dayjs';
import ReservationFilters from './ReservationFilters';
import ReservationCards from './ReservationCards';
import ReservationTable from './ReservationTable';
import ReservationCalendar from './ReservationCalendar';

const ReservationsManagement = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  // const [filters, setFilters] = useState({});

  useEffect(() => {
    if (user?.roles.includes('admin')) {
      fetchReservations();
    }
  }, [user]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const today = dayjs().format('YYYY-MM-DD');
      const response = await axios.get(`/api/reservations`);
      setReservations(response.data);
      setFilteredReservations(response.data);
    } catch (err) {
      setError('Errore nel caricamento delle prenotazioni');
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    // setFilters(newFilters);
    
    let filtered = [...reservations];

    // Apply filters
    if (newFilters.status) {
      filtered = filtered.filter(res => res.status === newFilters.status);
    }
    if (newFilters.paymentStatus) {
      filtered = filtered.filter(res => res.paymentStatus === newFilters.paymentStatus);
    }
    if (newFilters.reservableId) {
      filtered = filtered.filter(res => res.reservableId === newFilters.reservableId);
    }
    if (newFilters.userId) {
      filtered = filtered.filter(res => res.userId === newFilters.userId);
    }
    if (newFilters.dateFrom) {
      const fromDate = dayjs(newFilters.dateFrom).startOf('day').toDate();
      filtered = filtered.filter(res => new Date(res.date) >= fromDate);
    }
    if (newFilters.dateTo) {
      const toDate = dayjs(newFilters.dateTo).endOf('day').toDate();
      filtered = filtered.filter(res => new Date(res.date) <= toDate);
    }
    if (newFilters.search) {
      const searchLower = newFilters.search.toLowerCase();
      filtered = filtered.filter(res => 
        res.user?.name?.toLowerCase().includes(searchLower) ||
        res.user?.surname?.toLowerCase().includes(searchLower) ||
        res.reservable?.name?.toLowerCase().includes(searchLower) ||
        res.notes?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredReservations(filtered);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  if (!user?.roles.includes('admin')) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Accesso negato. Solo gli amministratori possono gestire le prenotazioni.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Gestione Prenotazioni
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestisci e monitora tutte le prenotazioni del sistema
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <ReservationFilters
          reservations={reservations}
          onFiltersChange={handleFiltersChange}
        />
      </Paper>

      {/* View Mode Selector */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {filteredReservations.length} prenotazion{filteredReservations.length !== 1 ? 'i' : 'e'}
        </Typography>
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
        >
          <ToggleButton value="cards" aria-label="cards view">
            <ViewModule sx={{ mr: 1 }} />
            Cards
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChart sx={{ mr: 1 }} />
            Tabella
          </ToggleButton>
          <ToggleButton value="calendar" aria-label="calendar view">
            <CalendarToday sx={{ mr: 1 }} />
            Calendario
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content based on view mode */}
      {viewMode === 'cards' && (
        <ReservationCards
          reservations={filteredReservations}
          onRefresh={fetchReservations}
        />
      )}
      
      {viewMode === 'table' && (
        <ReservationTable
          reservations={filteredReservations}
          onRefresh={fetchReservations}
        />
      )}
      
      {viewMode === 'calendar' && (
        <ReservationCalendar
          reservations={filteredReservations}
        />
      )}
    </Container>
  );
};

export default ReservationsManagement;